import { useState } from 'react';
import { AREAS, AREA_LABELS, CATEGORIES, CATEGORY_LABELS } from '~/lib/labels';

/** {slug,label} pairs for this city, served from city_areas rows. */
export interface AreaOption {
  slug: string;
  label: string;
}

/**
 * Directory filter box — labeled dropdowns + a Free checkbox + (optional)
 * keyword input. Collapsed by default behind a single "Filters" disclosure
 * button that shows an active-filter count. Same URL-param contract as before:
 * onChange({ area, category, cost, time, sort, when }) with the SAME param names
 * the loaders already read — area/category are CSV (single value here),
 * when/cost/time/sort are single. The loader query logic is unchanged.
 */

const WHEN_OPTIONS: { value: string; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'this-week', label: 'This week' },
  { value: 'this-weekend', label: 'This weekend' },
  { value: 'next-week', label: 'Next week' },
  { value: 'this-month', label: 'This month' },
  { value: 'all', label: 'All upcoming' },
];

const TIME_OPTIONS = [
  { value: '', label: 'Any time' },
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
];

const SORT_OPTIONS = [
  { value: '', label: 'Soonest first' },
  { value: 'title', label: 'A – Z' },
];

export interface FilterState {
  area: string; // csv
  category: string; // csv
  cost: string; // '' | 'free' | 'paid'
  time: string; // '' | 'morning' | 'afternoon' | 'evening'
  sort: string; // '' | 'title'
  when?: string; // preset (only when showWhen)
  q?: string; // applied keyword (for the active count)
}

/** Single value out of a possibly-CSV field (AI search can set multiple). */
function single(csv: string): string {
  const parts = csv.split(',').filter(Boolean);
  return parts.length === 1 ? (parts[0] ?? '') : '';
}

export function activeFilterCount(f: FilterState, includeWhen = false): number {
  let n = 0;
  n += f.area.split(',').filter(Boolean).length;
  n += f.category.split(',').filter(Boolean).length;
  if (f.cost) n += 1;
  if (f.time) n += 1;
  if (f.q && f.q.trim()) n += 1;
  if (includeWhen && f.when && f.when !== 'this-week' && f.when !== 'all') n += 1;
  return n;
}

interface KeywordSlot {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
}

export function FilterBox({
  value,
  onChange,
  showWhen = false,
  keyword,
  collapsible = false,
  defaultOpen = false,
  areas,
}: {
  value: FilterState;
  onChange: (patch: Record<string, string>) => void;
  showWhen?: boolean;
  keyword?: KeywordSlot;
  collapsible?: boolean;
  defaultOpen?: boolean;
  /**
   * This city's areas, from city_areas rows via /api/config. Optional so the
   * component still renders if a caller has not wired it yet; the fallback is
   * the bundled list, which is Tampa's. Any city that is not Tampa MUST pass
   * this — otherwise its filter offers St. Pete and Clearwater.
   */
  areas?: AreaOption[];
}) {
  const areaOptions: AreaOption[] =
    areas && areas.length > 0
      ? areas
      : AREAS.map((slug) => ({ slug, label: AREA_LABELS[slug] ?? slug }));
  const [open, setOpen] = useState(defaultOpen);
  const count = activeFilterCount(value, showWhen);
  const areaVal = single(value.area);
  const catVal = single(value.category);

  function clearAll() {
    onChange({
      area: '',
      category: '',
      cost: '',
      time: '',
      sort: '',
      q: '',
      ...(showWhen ? { when: 'all' } : {}),
    });
    keyword?.onChange('');
  }

  const grid = (
    <div className="filter-grid">
      {showWhen && (
        <div className="filter-field">
          <label className="filter-field-label" htmlFor="filter-when">
            When
          </label>
          <select
            id="filter-when"
            className="ui-select"
            value={value.when ?? 'all'}
            onChange={(e) => onChange({ when: e.target.value, from: '', to: '' })}
          >
            {WHEN_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="filter-field">
        <label className="filter-field-label" htmlFor="filter-area">
          Area
        </label>
        <select
          id="filter-area"
          className="ui-select"
          value={areaVal}
          onChange={(e) => onChange({ area: e.target.value })}
        >
          <option value="">All areas</option>
          {areaOptions.map((a) => (
            <option key={a.slug} value={a.slug}>
              {a.label}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-field">
        <label className="filter-field-label" htmlFor="filter-type">
          Type
        </label>
        <select
          id="filter-type"
          className="ui-select"
          value={catVal}
          onChange={(e) => onChange({ category: e.target.value })}
        >
          <option value="">All types</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {CATEGORY_LABELS[c]}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-field">
        <label className="filter-field-label" htmlFor="filter-time">
          Time of day
        </label>
        <select
          id="filter-time"
          className="ui-select"
          value={value.time}
          onChange={(e) => onChange({ time: e.target.value })}
        >
          {TIME_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-field">
        <label className="filter-field-label" htmlFor="filter-sort">
          Sort
        </label>
        <select
          id="filter-sort"
          className="ui-select"
          value={value.sort}
          onChange={(e) => onChange({ sort: e.target.value })}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-field">
        <span className="filter-field-label">Cost</span>
        <label className="filter-check" htmlFor="filter-free">
          <input
            id="filter-free"
            type="checkbox"
            checked={value.cost === 'free'}
            onChange={(e) => onChange({ cost: e.target.checked ? 'free' : '' })}
          />
          Free events only
        </label>
      </div>

      {keyword && (
        <div className="filter-field filter-field-wide">
          <label className="filter-field-label" htmlFor="filter-keyword">
            Keyword
          </label>
          <form
            className="filter-keyword-row"
            onSubmit={(e) => {
              e.preventDefault();
              keyword.onSubmit();
            }}
          >
            <input
              id="filter-keyword"
              className="ui-input"
              type="search"
              placeholder="Title, organizer, venue, description…"
              value={keyword.value}
              onChange={(e) => keyword.onChange(e.target.value)}
            />
            <button type="submit" className="ui-btn btn-secondary">
              Search
            </button>
          </form>
        </div>
      )}
    </div>
  );

  // ── Collapsible disclosure ──────────────────────────────
  if (collapsible) {
    return (
      <div className="filter-disclosure" role="group" aria-label="Filter events">
        <div className="filter-bar">
          <button
            type="button"
            className={
              count > 0
                ? 'ui-btn btn-secondary filter-toggle is-active'
                : 'ui-btn btn-secondary filter-toggle'
            }
            aria-expanded={open ? 'true' : 'false'}
            aria-controls="filter-panel"
            onClick={() => setOpen((v) => !v)}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden="true">
              <path
                d="M2 4h16M5 10h10M8 16h4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Filters
            {count > 0 && <span className="filter-count">{count}</span>}
            <svg
              className={open ? 'filter-caret is-open' : 'filter-caret'}
              width="11"
              height="7"
              viewBox="0 0 12 8"
              aria-hidden="true"
            >
              <path
                d="M1 1l5 5 5-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          {count > 0 && (
            <button type="button" className="btn-link small filter-clear" onClick={clearAll}>
              Clear all
            </button>
          )}
        </div>
        {open && (
          <div id="filter-panel" className="filter-box filter-panel">
            {grid}
          </div>
        )}
      </div>
    );
  }

  // ── Always-open panel (legacy) ──────────────────────────
  return (
    <div className="filter-box" role="group" aria-label="Filter events">
      <div className="filter-box-head">
        <p className="filter-box-title">Filter events</p>
        {count > 0 && (
          <button type="button" className="btn-link small filter-clear" onClick={clearAll}>
            Clear filters ({count})
          </button>
        )}
      </div>
      {grid}
    </div>
  );
}
